import { logger } from '@/utils/logger/logger';
import { bridgeErrorKind } from '@heidi/plugin-core';
import {
  hasAcceptedOpenNoteGeneration,
  hasPendingDocumentRequests,
  hasPendingNoteRequests,
} from '@heidi/session/data';

export type ExitCaptureStopHandlerResult = 'stopped' | 'nothingToStop';
export type ExitCaptureStopResult =
  | ExitCaptureStopHandlerResult
  | 'unavailable'
  | 'failed'
  | 'timedOut';
export type ExitCaptureStopCallSite = 'logout' | 'logoutWithoutRedirect' | 'closeApp';

type ExitCaptureStopRegistration = {
  handler: () => Promise<ExitCaptureStopHandlerResult>;
};

const manualCaptureStops = new Map<string, Set<symbol>>();
const manualCaptureStopListeners = new Set<() => void>();

let registration: ExitCaptureStopRegistration | null = null;
let inFlightHandler: Promise<ExitCaptureStopHandlerResult> | null = null;

export function trackManualCaptureStop(sessionId: string): () => void {
  const token = Symbol();
  const tokens = manualCaptureStops.get(sessionId) ?? new Set<symbol>();
  tokens.add(token);
  manualCaptureStops.set(sessionId, tokens);
  manualCaptureStopListeners.forEach((listener) => listener());

  let released = false;
  return () => {
    if (released) return;
    released = true;
    tokens.delete(token);
    if (tokens.size === 0) manualCaptureStops.delete(sessionId);
    manualCaptureStopListeners.forEach((listener) => listener());
  };
}

export function hasPendingManualCaptureStop(): boolean {
  return manualCaptureStops.size > 0;
}

export function hasPendingManualCaptureStopForSession(sessionId: string): boolean {
  return manualCaptureStops.has(sessionId);
}

export function resetManualCaptureStopsForTests(): void {
  manualCaptureStops.clear();
  manualCaptureStopListeners.forEach((listener) => listener());
}

export function hasPendingCaptureSettlement(): boolean {
  return (
    hasPendingManualCaptureStop() ||
    hasPendingNoteRequests() ||
    hasPendingDocumentRequests() ||
    hasAcceptedOpenNoteGeneration()
  );
}

export function waitForManualCaptureStops(
  deadlineAt: number
): Promise<'none' | 'settled' | 'timedOut'> {
  if (!hasPendingManualCaptureStop()) return Promise.resolve('none');

  return new Promise((resolve) => {
    const onChange = () => {
      if (!hasPendingManualCaptureStop()) settle('settled');
    };

    manualCaptureStopListeners.add(onChange);
    const timeout = setTimeout(
      () => settle('timedOut'),
      Math.max(0, deadlineAt - Date.now())
    );

    function settle(outcome: 'settled' | 'timedOut') {
      clearTimeout(timeout);
      manualCaptureStopListeners.delete(onChange);
      resolve(outcome);
    }
  });
}

function getOrStartHandler(
  activeRegistration: ExitCaptureStopRegistration
): Promise<ExitCaptureStopHandlerResult> {
  if (inFlightHandler) return inFlightHandler;

  const handlerPromise = Promise.resolve().then(activeRegistration.handler);
  inFlightHandler = handlerPromise;
  void handlerPromise.then(
    () => {
      if (inFlightHandler === handlerPromise) inFlightHandler = null;
    },
    () => {
      if (inFlightHandler === handlerPromise) inFlightHandler = null;
    }
  );
  return handlerPromise;
}

export function registerExitCaptureStop(
  handler: () => Promise<ExitCaptureStopHandlerResult>
): () => void {
  const nextRegistration = { handler };
  registration = nextRegistration;

  return () => {
    if (registration === nextRegistration) {
      registration = null;
    }
  };
}

export async function endActiveCaptureForExit(
  deadlineAt: number,
  callSite: ExitCaptureStopCallSite
): Promise<ExitCaptureStopResult> {
  const startedAt = Date.now();
  const activeRegistration = registration;
  let result: ExitCaptureStopResult;

  const handlerPromise =
    inFlightHandler ??
    (activeRegistration ? getOrStartHandler(activeRegistration) : null);
  if (!handlerPromise) {
    result = 'unavailable';
  } else {
    const handlerResult = handlerPromise.then(
      (value) =>
        Date.now() >= deadlineAt
          ? { kind: 'timedOut' as const }
          : { kind: 'result' as const, value },
      (error: unknown) =>
        Date.now() >= deadlineAt
          ? { kind: 'timedOut' as const }
          : { kind: 'failed' as const, error }
    );
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<{ kind: 'timedOut' }>((resolve) => {
      timeout = setTimeout(
        () => resolve({ kind: 'timedOut' }),
        Math.max(0, deadlineAt - Date.now())
      );
    });

    const outcome = await Promise.race([handlerResult, timedOut]);
    if (timeout !== undefined) clearTimeout(timeout);

    if (outcome.kind === 'timedOut') {
      result = 'timedOut';
    } else if (outcome.kind === 'failed') {
      result = 'failed';
      logger.error('DESKTOP_EXIT_CAPTURE_STOP_FAILED', {
        errorKind: bridgeErrorKind(outcome.error),
      });
    } else {
      result = outcome.value;
    }
  }

  logger.info('DESKTOP_EXIT_CAPTURE_STOP', {
    callSite,
    result,
    waitedMs: Math.max(0, Date.now() - startedAt),
  });
  return result;
}
